import Button from '@ui/buttons/button/button';
import SlideDownCard from '@ui/cards/slide-down-card/slide-down-card';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import { ICONS } from '@ui/icons/types';
import SelectSingle from '@ui/select/react-select/select-single';
import React, { memo, useCallback, useMemo, useState } from 'react';
import Icon from '@ui/icons/icon';
import classes from '../campaign-editor.module.css';
import { EmailTypeEnum, type IScheduledEmail } from '@/types/email.types';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectScheduledEmails } from '@/store/slices/scheduled-emails/selectors';
import { patchScheduledEmailCustomizations, patchScheduledEmailFields } from '@/store/slices/scheduled-emails';
import useLoadCampaigns from '../../use-load-campaigns';
import type { TranslateString } from '@/types/translation.types';
import { campaignTemplates } from '../../templates';
import useLanguageParams from '@/utils/use-language-params';
import isTranslateString from '@/utils/is-translate-string';
import { updateTranslateKey } from '@/utils/update-translation-key';
import { selectWorkingProject } from '@/store/slices/project/selectors';
import { campaignSelectTypes } from '../../utils';

interface Props {
  scheduledEmail: IScheduledEmail | undefined;
}
function CampaignEditorTextInputs({ scheduledEmail }: Props) {
  const [textOpen, setTextOpen] = useState(true);
  const scheduledEmails = useAppSelector(selectScheduledEmails);
  const workingProject = useAppSelector(selectWorkingProject);
  const dispatch = useAppDispatch();
  const language = useLanguageParams();
  useLoadCampaigns();
  const baseLanguage = workingProject?.settings.i18n?.base ?? 'en';

  const registrationDisabled = useMemo(
    () =>
      scheduledEmails?.some(
        (email) => email.uuid !== scheduledEmail?.uuid && email.email_type === EmailTypeEnum.event_registration_email
      ),
    [scheduledEmail?.uuid, scheduledEmails]
  );

  const onChangeTitle = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!scheduledEmail) return;
      dispatch(
        patchScheduledEmailCustomizations({
          uuid: scheduledEmail.uuid,
          fields: {
            // update the subject field
            subject: updateTranslateKey({
              translateString: scheduledEmail.customizations.v2.subject as TranslateString,
              language: language ?? null,
              baseLanguage,
              input: e.currentTarget.value,
            }),
            // if current language is base, update campaign title to match
            campaignTitle:
              language === baseLanguage
                ? e.currentTarget.value
                : (scheduledEmail.customizations.v2.campaignTitle as string),
          },
        })
      );
    },
    [baseLanguage, dispatch, language, scheduledEmail]
  );

  const onChangeType = useCallback(
    (value: string) => {
      if (!scheduledEmail) return;
      dispatch(
        patchScheduledEmailFields({
          uuid: scheduledEmail.uuid,
          fields: {
            email_type: value as EmailTypeEnum,
          },
        })
      );
    },
    [dispatch, scheduledEmail]
  );

  const onChangeTemplateType = useCallback(
    (value: string) => {
      if (!scheduledEmail) return;
      dispatch(
        patchScheduledEmailCustomizations({
          uuid: scheduledEmail.uuid,
          fields: {
            templateId: value,
          },
        })
      );
    },
    [dispatch, scheduledEmail]
  );

  const getTitle = () => {
    if (!language) return '';

    const subject = scheduledEmail?.working_customizations?.v2?.subject ?? scheduledEmail?.customizations?.v2?.subject;

    if (isTranslateString(subject)) {
      return String(subject[language] ?? subject.base ?? '');
    }

    return String(scheduledEmail?.customizations?.v2?.campaignTitle || '');
  };

  return (
    <Container padding="0 12px">
      <Container
        style={{ transition: 'border-radius 350ms' }}
        borderRadius={textOpen ? '16px' : '12px'}
        design="GlobalCont2"
      >
        <Container>
          <Button
            className="flex jc-space-between ai-center width-100-percent"
            onClick={() => setTextOpen((prev) => !prev)}
          >
            <Text className="H6" design="GlobalIcoText1">
              Content
            </Text>
            <div style={{ transform: textOpen ? `rotate(90deg)` : undefined }}>
              <Icon name={ICONS.ANGLE_RIGHT} size={12} />
            </div>
          </Button>
        </Container>
        <SlideDownCard autoGrow open={textOpen} animateOnInitialLoad={false}>
          <Container padding="0 0 8px 0">
            <div className={classes.fieldRow}>
              <InputWithOverlay
                label="Subject"
                designSize={'small'}
                inputProps={{
                  onChange: onChangeTitle,
                  value: getTitle(),
                }}
              />
            </div>
            <div className={classes.fieldRow}>
              <Text design="GlobalIcoText3" tag="label" className="L2">
                Campaign Type
              </Text>
              <SelectSingle
                value={scheduledEmail?.email_type ?? ''}
                options={campaignSelectTypes(registrationDisabled)}
                onChange={onChangeType}
                placeholder="Select Type"
                width="100%"
              />
            </div>
            <div className={classes.fieldRow}>
              <Text design="GlobalIcoText3" tag="label" className="L2">
                Template Type
              </Text>
              <SelectSingle
                value={scheduledEmail?.working_customizations?.v2?.templateId as string}
                options={campaignTemplates.map((c) => ({ value: c.uuid, label: c.title }))}
                onChange={onChangeTemplateType}
                placeholder="Select Template"
                width="100%"
              />
            </div>
          </Container>
        </SlideDownCard>
      </Container>
    </Container>
  );
}

export default memo(CampaignEditorTextInputs);
